import { Children, forwardRef, useEffect, useRef } from "react";
import { Button, ButtonBase, Checkbox, Dialog, IconButton, MenuItem, OutlinedInput, Select } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import "./SpacesHomeControls.css";

// Opt-in controls: shared screens keep their existing controls until migrated.
export function HomeButton({ className = "", ...props }) {
  return <Button variant="outlined" className={`iio-mui-button ${className}`} {...props}/>;
}

export function HomeIconButton({ className = "", ...props }) {
  return <IconButton className={`iio-mui-icon ${className}`} {...props}/>;
}

export function HomeAction({ className = "", ...props }) {
  return <ButtonBase className={`iio-mui-action ${className}`} {...props}/>;
}

export const HomeInput = forwardRef(function HomeInput({ className, maxLength, "aria-label": label,
  "aria-describedby": describedBy, "aria-invalid": invalid, inputProps, ...props }, ref) {
  return <OutlinedInput fullWidth className="iio-mui-input" inputRef={ref} error={!!invalid}
    inputProps={{ maxLength, "aria-label": label, "aria-describedby": describedBy, "aria-invalid": invalid, ...inputProps }} {...props}/>;
});

export function HomeCheckbox({ "aria-label": label, "aria-describedby": describedBy, ...props }) {
  return <Checkbox className="iio-mui-checkbox" inputProps={{ "aria-label": label, "aria-describedby": describedBy }} {...props}/>;
}

// Accept the same values and option labels as the existing native controls.
export function HomeSelect({ children, className, "aria-label": label, "aria-describedby": describedBy, ...props }) {
  const input = useRef(null);
  return <Select fullWidth displayEmpty className="iio-mui-input" IconComponent={ExpandMoreRounded} labelId={`${props.id}-label`}
    inputRef={input}
    SelectDisplayProps={{ "aria-labelledby": `${props.id}-label` }}
    inputProps={{ "aria-label": label, "aria-describedby": describedBy }}
    MenuProps={{ PaperProps: { className: "iio iio-home-menu" }, MenuListProps: { "aria-label": label },
      // Restore after the menu transition so the enclosing dialog can retain focus.
      TransitionProps: { onExited: () => input.current?.focus() },
    }} {...props}>
    {Children.toArray(children).filter(Boolean).map(option => <MenuItem key={option.props.value}
      value={option.props.value} disabled={option.props.disabled}>
      <span className="iio-home-option-label">{option.props.children}</span>
      {props.value === option.props.value && <CheckRounded aria-hidden="true" className="iio-home-option-check"/>}
    </MenuItem>)}
  </Select>;
}

export function HomeDialog({ children, ...props }) {
  const opener = useRef(typeof document !== "undefined" ? document.activeElement : null);
  useEffect(() => {
    const target = opener.current;
    return () => {
      // Conditional unmount plus an async page refresh can clear MUI's restore target.
      requestAnimationFrame(() => {
        if (target?.isConnected && document.activeElement === document.body && !document.querySelector(".MuiModal-root")) {
          target.focus({ preventScroll: true });
        }
      });
    };
  }, []);
  return <Dialog open fullWidth maxWidth="sm" PaperProps={{ className: "iio iio-home-dialog" }} {...props}>{children}</Dialog>;
}
